import React, { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, EnvelopeSimple, DeviceMobile, Image as ImageIcon, X, UsersThree, User } from "@phosphor-icons/react";

function ChatControls() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/platform").then(({ data }) => setS(data.settings)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async (patch) => {
    setBusy(true);
    try { const { data } = await api.put("/settings/platform", patch); setS(data.settings); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const mins = (secs) => Math.round((secs || 0) / 60);
  return (
    <div className="max-w-2xl space-y-4 rounded-2xl border border-white/10 bg-card p-5" data-testid="chat-controls">
      <div>
        <h2 className="font-display text-xl font-semibold">Chat message controls</h2>
        <p className="mt-1 text-sm text-muted-foreground">Control editing and deletion in iYeneh Messages. All time limits are enforced on the server.</p>
      </div>
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium">Allow editing messages</span>
        <input data-testid="cfg-edit-enabled" type="checkbox" checked={!!s.chat_edit_enabled} onChange={(e) => save({ chat_edit_enabled: e.target.checked })} className="h-5 w-9 accent-primary" />
      </label>
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium">Edit window (minutes)</span>
        <input data-testid="cfg-edit-window" type="number" min="0" disabled={busy} defaultValue={mins(s.chat_edit_window_secs)} key={`ew-${s.chat_edit_window_secs}`}
          onBlur={(e) => save({ chat_edit_window_secs: Math.max(0, parseInt(e.target.value || 0, 10)) * 60 })}
          className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      </label>
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium">Allow &quot;delete for everyone&quot;</span>
        <input data-testid="cfg-delall-enabled" type="checkbox" checked={!!s.chat_delete_everyone_enabled} onChange={(e) => save({ chat_delete_everyone_enabled: e.target.checked })} className="h-5 w-9 accent-primary" />
      </label>
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium">Delete-for-everyone window (minutes)</span>
        <input data-testid="cfg-delall-window" type="number" min="0" disabled={busy} defaultValue={mins(s.chat_delete_everyone_window_secs)} key={`dw-${s.chat_delete_everyone_window_secs}`}
          onBlur={(e) => save({ chat_delete_everyone_window_secs: Math.max(0, parseInt(e.target.value || 0, 10)) * 60 })}
          className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      </label>
    </div>
  );
}

export default function AdminMessaging() {
  const [channel, setChannel] = useState("email");
  const [audience, setAudience] = useState("all");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMedia((m) => [...m, data]); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const send = async () => {
    if (!message.trim() && media.length === 0) { toast.error("Add a message or media"); return; }
    if (audience === "one" && !to.trim()) { toast.error("Enter a recipient email or phone"); return; }
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/admin/message/send", { channel, audience, to: to.trim(), subject, message, media }); setResult(data); toast.success(`Sent ${data.sent} · failed ${data.failed}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const Toggle = ({ v, set, opts }) => (
    <div className="flex w-max rounded-full bg-secondary p-1">
      {opts.map((o) => (
        <button key={o.v} data-testid={`opt-${o.v}`} onClick={() => set(o.v)}
          className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${v === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          {o.icon} {o.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-messaging">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Messaging &amp; Broadcast</h1>
        <p className="mt-1 text-sm text-muted-foreground">Send an individual message or a mass broadcast — text, flyer, banner, audio or video. <strong>Email is free</strong>; SMS uses your wallet credit.</p>
      </div>

      <div className="max-w-2xl space-y-5 rounded-2xl border border-white/10 bg-card p-5">
        <div className="flex flex-wrap gap-4">
          <div><p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Channel</p>
            <Toggle v={channel} set={setChannel} opts={[{ v: "email", label: "Email · Free", icon: <EnvelopeSimple size={15} weight="fill" /> }, { v: "sms", label: "SMS · Paid", icon: <DeviceMobile size={15} weight="fill" /> }]} /></div>
          <div><p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Send to</p>
            <Toggle v={audience} set={setAudience} opts={[{ v: "all", label: "All customers", icon: <UsersThree size={15} weight="fill" /> }, { v: "one", label: "One person", icon: <User size={15} weight="fill" /> }]} /></div>
        </div>

        {audience === "one" && (
          <input data-testid="msg-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder={channel === "email" ? "customer@email.com" : "+2376XXXXXXXX"}
            className="w-full rounded-xl border border-white/10 bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
        )}
        {channel === "email" && (
          <input data-testid="msg-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
            className="w-full rounded-xl border border-white/10 bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
        )}
        <textarea data-testid="msg-body" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Write your message…"
          className="w-full rounded-xl border border-white/10 bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />

        <div>
          <label data-testid="msg-attach" className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold hover:border-primary">
            <ImageIcon size={16} /> Attach flyer / banner / audio / video
            <input type="file" accept="image/*,audio/*,video/*" className="hidden" onChange={upload} />
          </label>
          {media.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {media.map((m, i) => (
                <span key={i} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs">
                  {m.type === "image" ? "🖼" : m.type === "video" ? "🎥" : m.type === "audio" ? "🎤" : "📎"} {m.name?.slice(0, 18) || m.type}
                  <button onClick={() => setMedia((p) => p.filter((_, j) => j !== i))}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          {channel === "sms" && media.length > 0 && <p className="mt-1 text-xs text-muted-foreground">SMS sends your text + a short link to each attachment.</p>}
        </div>

        <button data-testid="msg-send" onClick={send} disabled={busy}
          className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
          <PaperPlaneTilt size={18} weight="fill" /> {busy ? "Sending…" : audience === "all" ? "Send to all customers" : "Send message"}
        </button>

        {result && (
          <div data-testid="msg-result" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-500">
            Sent <strong>{result.sent}</strong> · failed {result.failed} · {result.recipients} recipient(s){result.billed_cents ? ` · billed $${(result.billed_cents / 100).toFixed(2)}` : ""}
          </div>
        )}
      </div>

      <ChatControls />
    </div>
  );
}
